<script setup lang="ts">
/**
 * The Activity Center — https://app.notion.com/p/3c304e9a59288185892ef46b9483144f.
 *
 * The ONE component allowed to render copy and actions for long-running work. Every other surface
 * (list row, grid tile, chat chip) is a pointer that opens this and owns no strings of its own.
 *
 * It renders `ActivityTask` and nothing else — no knowledge of indexing, quota, workflows or research.
 * If this file ever needs `if (kind === 'indexing')`, the contract in `shared/activity/types.ts` was
 * too narrow and should be widened instead.
 *
 * Two form factors, one component:
 *   • ≥ sm — dropdown anchored beside its trigger, which since MAR-127 lives in the icon rail's brand
 *     row at the LEFT edge (MAR-135). It used to hang off the right edge of the app bar, where the
 *     trigger then was; when the bell moved and this did not, the panel opened on the far side of the
 *     screen from the button that opened it. The offset follows the rail's own width tokens, so it
 *     stays attached whether the rail is collapsed (56px) or expanded (224px).
 *   • < sm — FULL-SCREEN sheet with a close button top-right. Deliberately does NOT close on
 *     outside-click (there is no outside) and locks body scroll while open.
 */
import { onClickOutside } from '@vueuse/core'
import { FocusScope } from 'reka-ui'
import {
  X, Loader2, AlertCircle, CircleSlash, Check, FileText, Workflow, Search,
  Wrench, ShieldCheck, Table, ScanText, Gauge,
} from 'lucide-vue-next'
import { useActivityStore } from '~/stores/activity'
import { ACTIVITY_KINDS, type ActivityTask, type ActivityKind } from '~~/shared/activity/types'

const { t } = useI18n()
const activity = useActivityStore()

/** The trigger travels with the rail, so the panel's left offset has to travel with it too. */
const { railExpanded } = usePanelLayout()

// Every icon `ACTIVITY_KINDS` names. A kind whose icon is missing here silently renders as
// `FileText` — which `tool` did from the day it was added until #56.
const ICONS: Record<string, unknown> = {
  FileText, Workflow, Search, Wrench, ShieldCheck, Table, ScanText, Gauge,
}
function kindIcon(kind: ActivityKind) {
  return ICONS[ACTIVITY_KINDS[kind]?.icon ?? 'FileText'] ?? FileText
}

/** Groups, in the order a user needs them: decisions, then work in flight, then receipts. */
const groups = computed(() => [
  { key: 'attention', label: t('activity.needsAttention'), tasks: activity.visible.filter(x => x.state === 'attention') },
  { key: 'running', label: t('activity.inProgress'), tasks: activity.visible.filter(x => x.state === 'running' || x.state === 'queued') },
  { key: 'done', label: t('activity.finished'), tasks: activity.visible.filter(x => x.state === 'done') },
].filter(g => g.tasks.length > 0))

async function onAction(task: ActivityTask, actionId: string, kind: 'link' | 'run', to?: string) {
  if (kind === 'link') {
    await navigateTo(to ?? '/app')
    activity.close()
    return
  }
  await activity.run(task.id, actionId)
}

// Which form factor is mounted — mirrors ChatScopeBar.vue's local matchMedia-backed ref; no shared
// useMediaQuery composable exists yet. `sm` matches the Tailwind breakpoint the template branches on
// below, so this agrees with what the user actually sees rendered.
const isDesktop = ref(true)
let mql: MediaQueryList | null = null
const syncViewport = (e: MediaQueryList | MediaQueryListEvent) => { isDesktop.value = e.matches }
onMounted(() => {
  mql = window.matchMedia('(min-width: 640px)')
  syncViewport(mql)
  mql.addEventListener('change', syncViewport)
})
onUnmounted(() => mql?.removeEventListener('change', syncViewport))

/**
 * FocusScope's two hops — into the panel on open, back to the opener on close — belong to the modal
 * mobile sheet only. The desktop dropdown is non-modal: pulling focus into it on open, or back to the
 * bell after an outside click closed it, would steal focus from wherever the user just went.
 */
function onSheetAutoFocus(e: Event) {
  if (isDesktop.value) e.preventDefault()
}

// Body scroll lock, mobile only — the sheet covers the viewport, so a scrolling page behind it is
// both wrong and reachable by touch. The desktop dropdown does NOT cover the viewport, so it must
// never lock the page behind it. Bounded to the open state AND the viewport, and always released on
// unmount regardless of which viewport that happens on (so a resize-while-open can't strand the lock).
const locked = ref(false)
function lockScroll(on: boolean) {
  if (typeof document === 'undefined') return
  if (on === locked.value) return
  locked.value = on
  document.body.style.overflow = on ? 'hidden' : ''
}
watch(() => activity.open && !isDesktop.value, shouldLock => lockScroll(shouldLock))
onUnmounted(() => lockScroll(false))

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') activity.close()
}
onMounted(() => document.addEventListener('keydown', onKeydown))
onUnmounted(() => document.removeEventListener('keydown', onKeydown))

/**
 * Dismissal, and why it is NOT a full-screen click-catcher any more (#84).
 *
 * This used to render `<div class="hidden sm:block fixed inset-0" @click="close">` above the page.
 * An invisible sheet over the whole viewport intercepts far more than the click it was written for:
 * with the panel open, NO drop target underneath could be reached, because
 *
 *   • a native OS file drag hit the catcher, so `dragenter`/`dragover` never reached the target, and
 *   • an in-app item drag (the `MARCUS_DROP_MIME` payload) could not even START — the `mousedown`
 *     landed on the catcher instead of the draggable row, so the browser began no drag at all.
 *
 * That second half is why "close the panel when a drag starts" cannot be the whole fix: with the
 * catcher in place there is no drag to observe. So the catcher goes, and dismissal moves to
 * `onClickOutside` — the pattern the rest of the app's dropdowns already use (`FileExplorer`, the
 * docs sort menus, the case emoji picker). The trigger is excluded, or its own click would reopen
 * the panel the capture-phase handler just closed.
 *
 * On mobile the panel root IS the viewport, so a tap can never land outside it and the sheet keeps
 * its documented "does not close on outside click" behaviour with no `sm:` branch needed here.
 *
 * The trade this accepts: a click outside now also reaches the control it landed on, instead of
 * being swallowed. That is how every other dropdown in the app behaves.
 */
const panelRef = ref<HTMLElement | null>(null)
onClickOutside(panelRef, () => activity.close(), { ignore: ['[data-activity-trigger]'] })

/**
 * ...and a drag anywhere closes the panel, so the answer holds for EVERY drop target rather than
 * only the ones that happen to lie outside the panel's own 22rem box. Nothing inside the panel is
 * draggable, so there is no in-panel gesture to protect. `dragstart` covers in-app item drags,
 * `dragenter` covers a file arriving from outside the window.
 */
function onDragBegan() {
  if (activity.open) activity.close()
}
onMounted(() => {
  document.addEventListener('dragstart', onDragBegan)
  document.addEventListener('dragenter', onDragBegan)
})
onUnmounted(() => {
  document.removeEventListener('dragstart', onDragBegan)
  document.removeEventListener('dragenter', onDragBegan)
})
</script>

<template>
  <!-- Anchored beside the rail's trigger (top-left), so the panel opens where the eye already is.
       Full-screen on mobile — see the component doc. -->
  <div
    v-if="activity.open"
    ref="panelRef"
    class="fixed inset-0 z-50 sm:inset-auto sm:top-[calc(var(--header-h)+0.25rem)]"
    :class="railExpanded
      ? 'sm:left-[calc(var(--app-rail-width-expanded)+0.5rem)]'
      : 'sm:left-[calc(var(--app-rail-width)+0.5rem)]'"
  >
    <!-- No click-away sheet here: dismissal is `onClickOutside` in the script, because a full-viewport
         catcher swallowed every drag underneath it (#84).
         `aria-modal` only on the mobile sheet, which covers the viewport and locks its scroll; the
         desktop panel leaves the page live, so claiming modality there hid the page from screen
         readers (MAR-948).
         reka's FocusScope keeps that claim true for the keyboard: trapped and looping on mobile, with
         focus moved in on open and handed back on close; on desktop it is inert (no trap, and both
         auto-focus hops are cancelled), so the dropdown stays non-modal. -->
    <FocusScope
      as-child
      :trapped="!isDesktop"
      :loop="!isDesktop"
      @mount-auto-focus="onSheetAutoFocus"
      @unmount-auto-focus="onSheetAutoFocus"
    >
    <div
      class="relative flex flex-col bg-card border-border shadow-lg outline-none
             h-full w-full border-0
             sm:h-auto sm:max-h-[70vh] sm:w-[22rem] sm:rounded-xl sm:border"
      role="dialog"
      :aria-modal="isDesktop ? undefined : 'true'"
      :aria-label="t('activity.title')"
    >
      <header class="flex items-center gap-2 px-4 py-3 border-b sm:py-2.5">
        <span class="text-sm font-medium flex-1">{{ t('activity.title') }}</span>
        <button
          v-if="activity.settled.length"
          type="button"
          class="text-xs text-muted-foreground hover:text-foreground transition-colors"
          @click="activity.clearSettled()"
        >
          {{ t('activity.clear') }}
        </button>
        <!-- Top-right close: the only way out on mobile, and a convenience on desktop. -->
        <button
          type="button"
          class="-mr-1 p-1.5 rounded-md hover:bg-muted transition-colors"
          :aria-label="t('activity.close')"
          @click="activity.close()"
        >
          <X class="h-4 w-4" />
        </button>
      </header>

      <div class="flex-1 overflow-y-auto overscroll-contain">
        <section v-for="group in groups" :key="group.key">
          <h3 class="px-4 pt-3 pb-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            {{ group.label }} · {{ group.tasks.length }}
          </h3>

          <article
            v-for="task in group.tasks"
            :key="task.id"
            class="px-4 py-2.5 border-b last:border-b-0"
            :class="[
              // A dismissed row is shown on request only, so it reads as filed away: no attention
              // tint, and dimmed enough that it never competes with live work for the eye.
              // An `ask` row is an invitation, not a problem: it keeps the attention group's sort
              // position (a human still has to act) without the amber tint that reads as a fault.
              task.dismissed
                ? 'opacity-50'
                : task.state === 'attention' && (task.badge?.tone === 'ask' ? 'bg-accent-surface' : 'bg-warning/5'),
            ]"
          >
            <div class="flex items-center gap-2">
              <!-- A stalled or stopped job must not spin: the animation would claim work that is not happening. -->
              <Loader2 v-if="task.state === 'running'" class="h-3.5 w-3.5 animate-spin text-primary shrink-0" />
              <ScanText v-else-if="task.state === 'attention' && task.badge?.tone === 'ask'" class="h-3.5 w-3.5 text-accent-ink shrink-0" />
              <CircleSlash v-else-if="task.state === 'attention' && task.badge?.tone === 'warn'" class="h-3.5 w-3.5 text-warning-ink shrink-0" />
              <AlertCircle v-else-if="task.state === 'attention'" class="h-3.5 w-3.5 text-destructive shrink-0" />
              <Check v-else-if="task.state === 'done'" class="h-3.5 w-3.5 text-info shrink-0" />
              <component :is="kindIcon(task.kind)" v-else class="h-3.5 w-3.5 text-muted-foreground shrink-0" />

              <span class="text-[13px] truncate flex-1">{{ task.title }}</span>

              <span
                v-if="task.badge"
                class="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium"
                :class="{
                  'bg-warning/15 text-warning-ink': task.badge.tone === 'warn',
                  'bg-destructive/10 text-destructive': task.badge.tone === 'bad',
                  'bg-tertiary/10 text-info': task.badge.tone === 'good',
                  'bg-primary-lighter text-primary': task.badge.tone === 'work',
                  'bg-accent-surface text-accent-ink': task.badge.tone === 'ask',
                }"
              >{{ task.badge.text }}</span>
            </div>

            <p v-if="task.detail" class="mt-1 text-[11px] leading-relaxed text-muted-foreground">
              {{ task.detail }}
            </p>

            <!-- Positions, never a percentage: the pipelines report phases, so a number would be invented. -->
            <div v-if="task.phases?.length" class="mt-1.5 flex gap-1">
              <span
                v-for="phase in task.phases"
                :key="phase.label"
                class="h-0.5 flex-1 rounded-full"
                :class="phase.done ? 'bg-primary' : 'bg-muted'"
              />
            </div>

            <div v-if="task.actions?.length" class="mt-2 flex flex-wrap gap-1.5">
              <button
                v-for="action in task.actions"
                :key="action.id"
                type="button"
                class="h-7 px-2.5 text-[11px] rounded-md border border-input transition-colors sm:h-6"
                :class="action.variant === 'primary'
                  ? 'bg-primary text-primary-foreground border-primary hover:opacity-90'
                  : 'hover:bg-accent-lighter'"
                @click="onAction(task, action.id, action.kind, action.to)"
              >
                {{ action.label }}
              </button>
            </div>
          </article>
        </section>

        <p v-if="!groups.length" class="px-4 py-6 text-center text-xs text-muted-foreground">
          {{ t('activity.empty') }}
        </p>
      </div>

      <!-- Dismissal is durable, so without a way back a mis-click is permanent. Deliberately quiet:
           it is an escape hatch, not a control anyone needs on the way to something else. -->
      <footer class="border-t px-4 py-2">
        <button
          type="button"
          class="flex w-full items-center gap-2 text-[11px] text-muted-foreground/70 hover:text-muted-foreground transition-colors"
          :aria-pressed="activity.showDismissed"
          @click="activity.toggleDismissed()"
        >
          <span
            class="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-[3px] border transition-colors"
            :class="activity.showDismissed ? 'bg-accent border-accent-ink text-accent-foreground' : 'border-input'"
          >
            <Check v-if="activity.showDismissed" class="h-2.5 w-2.5" />
          </span>
          {{ t('activity.showDismissed') }}
        </button>
      </footer>
    </div>
    </FocusScope>
  </div>
</template>
